import { useCallback, useEffect, useMemo, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { fetchAuthSession, signInWithRedirect, signOut as signOutUser } from 'aws-amplify/auth';
import { Button } from '@/components/ui/button';
import { AppShell } from '@/components/layout/app-shell';
import { RepoBrowserPage } from '@/pages/repo-browser-page';
import { RepoListPage } from '@/pages/repo-list-page';
import { NotFoundPage } from '@/pages/not-found';
import { ensureAmplifyConfigured, loadRuntimeConfig, type RuntimeConfig } from '@/lib/runtime-config';
import { getTenantIdFromToken, getUserDisplayNameFromToken } from '@/lib/auth';

interface AuthState {
  isLoading: boolean;
  isAuthenticated: boolean;
  tenantId: string | null;
  userName: string | null;
  error: string | null;
}

const useAuthState = () => {
  const [state, setState] = useState<AuthState>({
    isLoading: true,
    isAuthenticated: false,
    tenantId: null,
    userName: null,
    error: null
  });

  useEffect(() => {
    let cancelled = false;

    const initialise = async () => {
      try {
        await ensureAmplifyConfigured();
        const session = await fetchAuthSession();
        const token = session.tokens?.idToken?.toString() ?? null;
        const tenantId = getTenantIdFromToken(token);
        const userName = getUserDisplayNameFromToken(token);

        if (!cancelled) {
          setState({
            isLoading: false,
            isAuthenticated: Boolean(session.tokens?.idToken),
            tenantId,
            userName,
            error: tenantId ? null : 'Missing tenant context in your session.'
          });
        }
      } catch (error) {
        if (!cancelled) {
          setState({
            isLoading: false,
            isAuthenticated: false,
            tenantId: null,
            userName: null,
            error: error instanceof Error ? error.message : 'Unable to check authentication state'
          });
        }
      }
    };

    void initialise();

    return () => {
      cancelled = true;
    };
  }, []);

  return state;
};

const SignInPrompt = ({ onSignIn }: { onSignIn: () => Promise<void> }) => (
  <div className="flex min-h-screen items-center justify-center px-6">
    <div className="w-full max-w-md rounded-2xl border border-border bg-card p-8 text-center shadow-sm">
      <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Filesync</p>
      <h1 className="mt-2 text-2xl font-semibold">Sign in to your portal</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Use your organisation account to access repo status and downloads.
      </p>
      <Button
        className="mt-6 w-full"
        onClick={() => {
          void onSignIn();
        }}
      >
        Sign in with Entra ID
      </Button>
    </div>
  </div>
);

const AuthenticatedApp = ({
  runtimeConfig,
  tenantId,
  onSignOut,
  userName
}: {
  runtimeConfig: RuntimeConfig;
  tenantId: string;
  onSignOut: () => Promise<void>;
  userName: string | null;
}) => (
  <BrowserRouter>
    <AppShell onSignOut={onSignOut} userName={userName}>
      <Routes>
        <Route path="/" element={<Navigate to="/repos" replace />} />
        <Route
          path="/repos"
          element={<RepoListPage runtimeConfig={runtimeConfig} tenantId={tenantId} />}
        />
        <Route
          path="/repos/:repoId"
          element={<RepoBrowserPage runtimeConfig={runtimeConfig} tenantId={tenantId} />}
        />
        <Route
          path="/repos/:repoId/files/*"
          element={<RepoBrowserPage runtimeConfig={runtimeConfig} tenantId={tenantId} />}
        />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </AppShell>
  </BrowserRouter>
);

function App() {
  const [runtimeConfig, setRuntimeConfig] = useState<RuntimeConfig | null>(null);
  const [configError, setConfigError] = useState<string | null>(null);
  const authState = useAuthState();

  useEffect(() => {
    let cancelled = false;

    const fetchConfig = async () => {
      try {
        const config = await loadRuntimeConfig();
        if (!cancelled) {
          setRuntimeConfig(config);
        }
      } catch (error) {
        if (!cancelled) {
          setConfigError(error instanceof Error ? error.message : 'Unable to load runtime configuration');
        }
      }
    };

    void fetchConfig();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleSignIn = useCallback(async () => {
    await ensureAmplifyConfigured();
    await signInWithRedirect({
      provider: {
        custom: 'EntraID'
      }
    });
  }, []);

  const handleSignOut = useCallback(async () => {
    await ensureAmplifyConfigured();
    await signOutUser();
    window.location.href = '/';
  }, []);

  const content = useMemo(() => {
    if (configError) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-background px-6 text-center">
          <div>
            <p className="text-lg font-semibold text-error">Configuration error</p>
            <p>{configError}</p>
          </div>
        </div>
      );
    }

    if (authState.isLoading || !runtimeConfig) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-background">
          <p className="text-muted-foreground">Preparing your session...</p>
        </div>
      );
    }

    if (!authState.isAuthenticated) {
      return <SignInPrompt onSignIn={handleSignIn} />;
    }

    if (!authState.tenantId) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-background px-6 text-center">
          <div>
            <p className="text-lg font-semibold text-error">Tenant context missing</p>
            <p>{authState.error ?? 'Your session is missing tenant details.'}</p>
          </div>
        </div>
      );
    }

    return (
      <AuthenticatedApp
        runtimeConfig={runtimeConfig}
        tenantId={authState.tenantId}
        onSignOut={handleSignOut}
        userName={authState.userName}
      />
    );
  }, [authState, configError, handleSignIn, handleSignOut, runtimeConfig]);

  return content;
}

export default App;
